// Sparkline: a word-sized trend for a table cell (dataviz: small multiples, no axes, no hover
// layer). Inline SVG, so it renders without layout measurement; the line wears the first
// series token and an optional reference (target) is dashed in the muted foreground. A null
// value breaks the line: missing data is never drawn as zero. The accessible name carries the
// numbers, so the chart is never the only way to read them.
import { cn } from "../cn";

export type SparklineProps = {
  values: readonly (number | null)[];
  /** Same length as `values`, e.g. the target per period. */
  reference?: readonly (number | null)[];
  /** Accessible name, e.g. "Interviews booked, last 8 weeks: 3, 5, 6". */
  label: string;
  className?: string;
};

const W = 100;
const H = 24;
const PAD = 2;

function segments(
  values: readonly (number | null)[],
  x: (i: number) => number,
  y: (v: number) => number,
): string[] {
  const out: string[] = [];
  let current: string[] = [];
  values.forEach((v, i) => {
    if (v === null || !Number.isFinite(v)) {
      if (current.length) out.push(current.join(" "));
      current = [];
      return;
    }
    current.push(`${x(i).toFixed(2)},${y(v).toFixed(2)}`);
  });
  if (current.length) out.push(current.join(" "));
  return out;
}

export function Sparkline({ values, reference, label, className }: SparklineProps) {
  const all = [...values, ...(reference ?? [])].filter(
    (v): v is number => v !== null && Number.isFinite(v),
  );
  const min = all.length ? Math.min(...all) : 0;
  const max = all.length ? Math.max(...all) : 1;
  const span = max - min || 1;
  const n = Math.max(values.length, reference?.length ?? 0);
  const x = (i: number) => (n <= 1 ? W / 2 : PAD + (i * (W - 2 * PAD)) / (n - 1));
  const y = (v: number) => H - PAD - ((v - min) / span) * (H - 2 * PAD);
  const line = segments(values, x, y);
  const ref = reference ? segments(reference, x, y) : [];
  const last = [...values].reverse().findIndex((v) => v !== null && Number.isFinite(v));
  const lastIndex = last === -1 ? -1 : values.length - 1 - last;
  const lastValue = lastIndex === -1 ? null : values[lastIndex];
  const present = (i: number) => {
    const v = values[i];
    return v !== null && v !== undefined && Number.isFinite(v);
  };
  // The last observation already has its own dot when it stands alone.
  const lastIsAlone = lastIndex !== -1 && !present(lastIndex - 1);

  return (
    <svg
      data-slot="sparkline"
      role="img"
      aria-label={label}
      viewBox={`0 0 ${W} ${H}`}
      preserveAspectRatio="none"
      className={cn("h-6 w-24 overflow-visible", className)}
    >
      {ref.map((points) => (
        <polyline
          key={`ref-${points}`}
          data-series="reference"
          points={points}
          fill="none"
          stroke="var(--muted-foreground)"
          strokeWidth={1}
          strokeDasharray="3 3"
          vectorEffect="non-scaling-stroke"
        />
      ))}
      {line.map((points) => {
        if (points.includes(" "))
          return (
            <polyline
              key={points}
              data-series="value"
              points={points}
              fill="none"
              stroke="var(--chart-1)"
              strokeWidth={2}
              strokeLinejoin="round"
              strokeLinecap="round"
              vectorEffect="non-scaling-stroke"
            />
          );
        // An observation between two gaps has no line to sit on: it is drawn as a dot.
        const [cx, cy] = points.split(",");
        return (
          <circle
            key={points}
            data-series="point"
            cx={cx}
            cy={cy}
            r={1.75}
            fill="var(--chart-1)"
            vectorEffect="non-scaling-stroke"
          />
        );
      })}
      {lastValue !== null && lastValue !== undefined && !lastIsAlone ? (
        <circle
          data-series="last"
          cx={x(lastIndex)}
          cy={y(lastValue)}
          r={1.75}
          fill="var(--chart-1)"
          vectorEffect="non-scaling-stroke"
        />
      ) : null}
    </svg>
  );
}
